import { captureContactFormClientFailure } from './contactSentry';

/**
 * @typedef {'contact' | 'question'} ContactFormType
 *
 * @typedef {Object} ContactFormData
 * @property {ContactFormType} type
 * @property {string} [name]
 * @property {string} [email]
 * @property {string} [inquiryType]
 * @property {string} [subject]
 * @property {string} message
 */

/**
 * @typedef {import('./contactSentry').ContactSubmitMeta} ContactSubmitMeta
 */

/**
 * @param {Response} response
 * @returns {Promise<unknown>}
 */
async function readApiResponseBody(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Submits a contact form to the API.
 * @param {ContactFormData} data
 * @param {ContactSubmitMeta} meta
 * @returns {Promise<void>}
 */
export async function submitContactForm(data, meta) {
  let response;
  try {
    response = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  } catch (error) {
    const wrapped =
      error instanceof Error ? error : new Error('Failed to submit contact form');
    captureContactFormClientFailure(wrapped, meta, data);
    throw wrapped;
  }

  if (!response.ok) {
    const apiResponse = await readApiResponseBody(response);
    const apiMessage =
      apiResponse &&
      typeof apiResponse === 'object' &&
      apiResponse !== null &&
      'message' in apiResponse &&
      typeof apiResponse.message === 'string'
        ? apiResponse.message
        : 'Failed to submit';
    const submitError = new Error(apiMessage);
    captureContactFormClientFailure(submitError, meta, data, {
      httpStatus: response.status,
      apiResponse,
    });
    throw submitError;
  }
}
